import MomentUtils from "@date-io/moment";
import { CssBaseline } from "@material-ui/core";
import { MuiThemeProvider } from "@material-ui/core/styles";
import { MuiPickersUtilsProvider } from "@material-ui/pickers";
import React from "react";
import { useIdleTimer } from "react-idle-timer";
import { Provider, useDispatch } from "react-redux";
import { ThemeProvider } from "styled-components";
import { getType } from "typesafe-actions";
import SnackbarNotification from "../common/components/notification";
import Spinner from "../common/components/spinner";
import theme from "../common/themes/theme";
import { logoutActions } from "../features/account/login/login.actions";
import { session } from "../features/account/login/login.api";
import { initializeAuthActions } from "../common/actions/auth.actions";
import { trackLogoutTrigger } from "../common/utils/logoutDiagnostics";
import { rootSelector } from "../common/selectors/selectors";
import useAppendScript from "../utils/appendScript";
import AppRouter from "./AppRouter";
import store from "./store";

const App: React.FC = () => {
  const dispatch = useDispatch();
  const authState = rootSelector((state) => state.auth);
  //useAppendScript('https://cdn.userway.org/widget.js', true, [['data-account', 'sMV0ek6rkd'], ['cookie_flags', 'samesite=none;secure']]);
  useAppendScript("https://code.jquery.com/jquery-3.7.1.min.js", true);

  // SECURITY: Initialize XSRF-TOKEN cookie on app bootstrap
  // This must happen before any state-changing API calls (POST/PUT/PATCH/DELETE)
  React.useEffect(() => {
    session().catch(() => {
      // Silently fail - session endpoint may return 401 if not authenticated, which is OK
      // The cookie will be set on successful authentication
    });
  }, []);

  // Initialize auth state from HttpOnly cookie on app load
  // Cookie-based auth only. No token storage in JS.
  // Empty dependency array ensures this runs only once on mount
  React.useEffect(() => {
    dispatch({ type: getType(initializeAuthActions.request) });
  }, []);

  React.useEffect(() => {
    const script = document.createElement("script");
    script.setAttribute("data-account", "sMV0ek6rkd");
    script.setAttribute("cookie_flags", "samesite=none;secure");
    script.src = "https://cdn.userway.org/widget.js";
    script.async = true;
    //script.crossOrigin = '';
    document.body.appendChild(script);
  }, []);

  const handleOnIdle = (event: Event) => {
    if (authState.isAuthenticated === true && 
        Array.isArray(authState.roles) && 
        authState.roles.length > 0) {
      trackLogoutTrigger('idle_timer', { idleTimeout: '10min' });
      dispatch({ type: getType(logoutActions.request) });
    }
  };

  const { getRemainingTime, getLastActiveTime } = useIdleTimer({
    timeout: 1000 * 60 * 10,
    onIdle: handleOnIdle,
    //onActive: handleOnActive,
    //onAction: handleOnAction,
    debounce: 500,
  });

  return (
    <MuiPickersUtilsProvider utils={MomentUtils}>
      <div className="App">
        <CssBaseline />
        <Spinner />
        <SnackbarNotification />
        <AppRouter />
      </div>
    </MuiPickersUtilsProvider>
  );
};

export default () => (
  <MuiThemeProvider theme={theme}>
    <ThemeProvider theme={theme}>
      <Provider store={store}>
        <App />
      </Provider>
    </ThemeProvider>
  </MuiThemeProvider>
);
